import type { Tone } from '../../components/ui/index.js';

/**
 * Presentation helpers for assignment and attempt states.
 *
 * DELIBERATELY NO GREEN FOR ANY RESULT STATE. "Awaiting review" is not good
 * news about the material and neither is anything else here: approval does
 * not mean the material passed (`CLAUDE.md` section 8), and nothing in this
 * phase even approves. So no state renders in the success tone.
 */
export function toneForAssignmentStatus(status: string): Tone {
  if (status === 'in_progress') return 'info';
  if (status === 'temporarily_stopped') return 'warning';
  // D278: waiting on ANOTHER person (reviewer, approver) is the purple
  // waiting tone, kept apart from work in progress.
  if (status === 'awaiting_review') return 'waiting';
  if (status === 'awaiting_approval') return 'waiting';
  // Phase 9. Work that came back needs attention; it is not a verdict on the
  // material, so it is amber rather than red.
  if (status === 'returned_for_correction') return 'warning';
  if (status === 'retest_required') return 'warning';
  // `approved` is DELIBERATELY neutral. See the note above: a green badge here
  // would be read across the room as "the material passed" (D104).
  if (status === 'cancelled') return 'danger';
  return 'neutral';
}

export function toneForAttemptStatus(status: string): Tone {
  if (status === 'draft') return 'warning';
  if (status === 'returned') return 'warning';
  if (status === 'retest_required') return 'warning';
  // D278: submitted and technically accepted wait on a reviewer or approver.
  if (status === 'submitted') return 'waiting';
  if (status === 'technically_accepted') return 'waiting';
  if (status === 'discontinued') return 'danger';
  // `approved` falls through to neutral on purpose, for the same reason.
  return 'neutral';
}

/**
 * The tone of one entry in a revision comparison.
 *
 * A comparison describes what a PERSON changed in a record. None of these
 * tones is a judgement of the work, and none of them is green: this is
 * bookkeeping, not a verdict.
 */
export function toneForChangeKind(kind: string): Tone {
  if (kind === 'added') return 'info';
  if (kind === 'removed') return 'warning';
  if (kind === 'changed') return 'warning';
  return 'neutral';
}

/** The test's name in the interface language, falling back to the other. */
export function testName(
  test: { readonly nameAr: string | null; readonly nameEn: string | null; readonly code?: string | null },
  locale: string,
): string {
  const preferred = locale === 'ar' ? test.nameAr ?? test.nameEn : test.nameEn ?? test.nameAr;
  return preferred ?? test.code ?? '';
}

/** An instrument's ONE name, as typed (D183) — never chosen by language. */
export function equipmentName(item: { readonly name: string | null }): string {
  return item.name ?? '';
}

/**
 * An instrument's name as a FROZEN result revision recorded it.
 *
 * Revisions keep the two-slot shape they were written with (D186) — they are
 * part of an approved record and are never rewritten. Since migration 0012 a
 * new revision carries the one name in both slots, so this shows it once; an
 * older revision still shows what it recorded.
 */
export function recordedEquipmentName(
  item: { readonly nameAr: string | null; readonly nameEn: string | null },
  locale: string,
): string {
  return (locale === 'ar' ? item.nameAr ?? item.nameEn : item.nameEn ?? item.nameAr) ?? '';
}

/**
 * A calendar date (YYYY-MM-DD) for display.
 *
 * Formatted in UTC on purpose: a due date or a calibration valid-until date is
 * a DAY, not an instant, and rendering it through the browser's own timezone
 * would show the previous day for anybody west of Greenwich.
 */
export function formatCalendarDate(date: string | null, locale: string): string | null {
  if (!date) return null;
  const parsed = new Date(`${date}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return date;
  return new Intl.DateTimeFormat(locale === 'ar' ? 'ar' : 'en-GB', {
    timeZone: 'UTC',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(parsed);
}
